/**
 * Room themes.
 *
 * A theme is a *name*, not a stylesheet. The server stores `{preset: "slate"}`
 * and the colours live here, in the client. Letting a room store arbitrary
 * colour strings would make decorating a room a way to inject styles into
 * every visitor's page. Unknown names fall back rather than rendering anything
 * a host supplied.
 *
 * The palettes are pulled from physical things — slate, warm paper, olive
 * canvas, oxidised copper — rather than saturated screen colours. Accents are
 * muted on purpose: the room's contents are the video and the things people
 * put in it, so the furniture around them should stay quiet.
 */

export type ThemeName = "slate" | "paper" | "olive" | "copper" | "ink";

export type ThemeDef = {
  name: ThemeName;
  label: string;
  swatch: string;
  vars: Record<string, string>;
};

export const THEMES: Record<ThemeName, ThemeDef> = {
  slate: {
    name: "slate",
    label: "Slate",
    swatch: "linear-gradient(135deg, #3d4450, #6b7280)",
    vars: {
      "--bg": "#16181c",
      "--bg-raised": "#1e2126",
      "--bg-sunken": "#0f1114",
      "--edge": "#2f343c",
      "--text": "#e6e8ec",
      "--text-dim": "#8b929e",
      "--accent": "#7d8794",
      "--accent-text": "#0f1114",
      "--glow": "rgba(125, 135, 148, 0.25)",
    },
  },
  paper: {
    name: "paper",
    label: "Paper",
    swatch: "linear-gradient(135deg, #e8e0d2, #c4b49a)",
    vars: {
      "--bg": "#f4f1ea",
      "--bg-raised": "#fbfaf6",
      "--bg-sunken": "#e6e1d6",
      "--edge": "#d6cfc0",
      "--text": "#26231d",
      "--text-dim": "#726c60",
      "--accent": "#9c7a4d",
      "--accent-text": "#fbfaf6",
      "--glow": "rgba(156, 122, 77, 0.2)",
    },
  },
  olive: {
    name: "olive",
    label: "Olive",
    swatch: "linear-gradient(135deg, #3f4a33, #78855f)",
    vars: {
      "--bg": "#14170f",
      "--bg-raised": "#1c2015",
      "--bg-sunken": "#0d0f09",
      "--edge": "#2f3524",
      "--text": "#e7e9de",
      "--text-dim": "#8d9480",
      "--accent": "#8b9a66",
      "--accent-text": "#14170f",
      "--glow": "rgba(139, 154, 102, 0.22)",
    },
  },
  copper: {
    name: "copper",
    label: "Copper",
    swatch: "linear-gradient(135deg, #5c3a2a, #b0714c)",
    vars: {
      "--bg": "#191210",
      "--bg-raised": "#221917",
      "--bg-sunken": "#0f0b0a",
      "--edge": "#3b2b25",
      "--text": "#ece2dc",
      "--text-dim": "#9b8a80",
      "--accent": "#b0714c",
      "--accent-text": "#191210",
      "--glow": "rgba(176, 113, 76, 0.25)",
    },
  },
  ink: {
    name: "ink",
    label: "Ink",
    swatch: "linear-gradient(135deg, #1b2a3d, #3f5b7d)",
    vars: {
      "--bg": "#111519",
      "--bg-raised": "#182028",
      "--bg-sunken": "#0b0e12",
      "--edge": "#26323e",
      "--text": "#e2e8ee",
      "--text-dim": "#87939f",
      "--accent": "#5b7fa6",
      "--accent-text": "#0b0e12",
      "--glow": "rgba(91, 127, 166, 0.24)",
    },
  },
};

export const DEFAULT_THEME: ThemeName = "slate";

export function themeFrom(theme: Record<string, string> | undefined | null): ThemeDef {
  const preset = theme?.preset as ThemeName | undefined;
  return (preset && THEMES[preset]) || THEMES[DEFAULT_THEME];
}

/** Applies a theme's variables to an element. */
export function applyTheme(el: HTMLElement, theme: ThemeDef) {
  for (const [key, value] of Object.entries(theme.vars)) {
    el.style.setProperty(key, value);
  }
}
